import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, ArrowRight, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { posts } from '../lib/blog/posts';
import Footer from '../components/landing/Footer';

export default function Blog() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'uz') as 'uz' | 'ru' | 'en';

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-4 pt-6 pb-16 md:pb-20">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-300 hover:text-white text-sm mb-10 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('backToHome')}
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl md:text-4xl font-bold">{t('blogTitle')}</h1>
          </div>
          <p className="text-lg text-blue-200 max-w-xl">{t('blogDescription')}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 -mt-8">
        {/* Featured post */}
        <Link
          to={`/blog/${featured.slug}`}
          className="block bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all mb-8 group"
        >
          <div className="p-6 md:p-8">
            <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full mb-4 uppercase tracking-wide">
              {lang === 'uz' ? 'Tavsiya etilgan' : lang === 'ru' ? 'Рекомендуем' : 'Featured'}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
              {featured.title[lang]}
            </h2>
            <p className="text-gray-600 mb-5 text-base leading-relaxed line-clamp-3">
              {featured.description[lang]}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {new Date(featured.publishedAt).toLocaleDateString(lang === 'uz' ? 'uz-UZ' : lang === 'ru' ? 'ru-RU' : 'en-US', {
                    year: 'numeric', month: 'short', day: 'numeric'
                  })}
                </span>
                <span className="flex items-center gap-1.5">
                  <Tag className="w-4 h-4" />
                  {featured.tags[0]}
                </span>
              </div>
              <span className="flex items-center gap-1 text-blue-600 font-medium text-sm group-hover:gap-2 transition-all">
                {t('readMore')} <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </Link>

        {/* Rest of posts */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {rest.map(post => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all flex flex-col"
            >
              <div className="flex flex-wrap gap-1.5 mb-3">
                {post.tags.slice(0, 2).map(tag => (
                  <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors leading-snug">
                {post.title[lang]}
              </h3>
              <p className="text-gray-500 text-sm mb-4 line-clamp-2 flex-1">
                {post.description[lang]}
              </p>
              <div className="flex items-center justify-between text-xs text-gray-400 pt-3 border-t border-gray-50">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(post.publishedAt).toLocaleDateString(lang === 'uz' ? 'uz-UZ' : lang === 'ru' ? 'ru-RU' : 'en-US', {
                    month: 'short', day: 'numeric'
                  })}
                </span>
                <span className="text-blue-600 font-medium flex items-center gap-1 group-hover:gap-1.5 transition-all">
                  {t('readMore')} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
